import {
  Box,
  Center,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import {
  ContentNode,
  ContentNodeUtils,
  DataTypeConfig,
  DataTypeConfigUtils,
  DataTypeLocator,
} from '@helo/cms'
import { Assert, List, Value, pipe } from '@helo/utils'
import { useMemo } from 'react'

import { Icon } from '../../components/Icons/Icon'
import { Link } from '../../components/Link/Link'
import { HorizontalChart } from '../../components/charts/HorizontalChart/HorizontalChart'
import { LineChart } from '../../components/charts/LineChart/LineChart'
import { PieChart } from '../../components/charts/PieChart/PieChart'
import { VerticalBarChart } from '../../components/charts/VerticalBarChart/VerticalBarChart'
import { ChartBarLegendItem } from '../../components/charts/common/ChartBarLegendItem'
import { ChartLineLegendItem } from '../../components/charts/common/ChartLineLegendItem'
import { ChartSubtitle } from '../../components/charts/common/ChartSubtitle'
import { ChartTitle } from '../../components/charts/common/ChartTitle'
import type { ChartTitleVariant } from '../../components/charts/common/ChartTitle'
import {
  CHART_FIGMA_TEAL_UNLADING,
  CHART_HEIGHT,
  CHART_TITLE_MIN_WIDTH,
  HORIZONTAL_BAR_SIZE,
  SNAPSHOT_CHART_HEIGHT,
  SNAPSHOT_CHART_TITLE_CONTENT_GAP,
  SNAPSHOT_CHART_TITLE_PADDING_LEFT,
  SNAPSHOT_VIEW_IN_LIST_LABEL,
  type SnapshotAngledAxisBand,
} from '../../components/charts/common/chart.constants'
import {
  ChartData,
  ChartValue,
} from '../../components/charts/common/chart.types'
import { sortChartRowsByCategoryDate } from '../../components/charts/common/chartCategoryDate'
import type { ChartValueFormat } from '../../components/charts/common/chartFormat'
import { useAppStore } from '../../store/appStore/appStore'
import type { ViewUrlState } from '../../store/appStore/view/viewSlice.types'
import { createWidget } from '../widget-utils'

type ChartType = 'verticalBar' | 'line' | 'horizontal' | 'pie'

/**
 * Generic fallback when chart-type-specific inference does not apply (e.g. extra series).
 * Port-to-Port Figma: do **not** lead with blue+light-blue — that breaks line/MoM and
 * horizontal/unlading when CMS omits `color`.
 */
const CHART_INFERENCE_COLOR_PALETTE = [
  '#26A7F7',
  '#E6F0FE',
  '#FF6C47',
  '#CA2265',
  CHART_FIGMA_TEAL_UNLADING,
  '#4D78BC',
  '#81C99B',
  '#286693',
] as const

/** Figma Port-to-Port Transit: MoM lines = orange + magenta (not blue/white). */
const INFERENCE_LINE = ['#FF6C47', '#CA2265'] as const
/** Figma: unlading horizontal bar = teal. */
const INFERENCE_HORIZONTAL = [CHART_FIGMA_TEAL_UNLADING] as const
/**
 * Grouped vertical bars: avg blue + median (Figma: visible on dark; `#E6F0FE` clips against grid).
 */
const INFERENCE_VERTICAL_GROUPED = ['#26A7F7', '#7EB8E0'] as const
/** Stacked pickups: steel blue (before) + soft green (after). */
const INFERENCE_VERTICAL_STACKED = ['#4D78BC', '#81C99B'] as const

const inferenceColorForSeries = (
  chartType: ChartType,
  stacked: boolean,
  index: number,
): string => {
  if (chartType === 'line') {
    return INFERENCE_LINE[index % INFERENCE_LINE.length]
  }
  if (chartType === 'horizontal') {
    return INFERENCE_HORIZONTAL[index % INFERENCE_HORIZONTAL.length]
  }
  if (chartType === 'verticalBar') {
    const pal = stacked
      ? INFERENCE_VERTICAL_STACKED
      : INFERENCE_VERTICAL_GROUPED
    return pal[index % pal.length]
  }
  return CHART_INFERENCE_COLOR_PALETTE[
    index % CHART_INFERENCE_COLOR_PALETTE.length
  ]
}

export type ChartSeriesConfig = {
  measureLocator?: DataTypeLocator
  locator?: DataTypeLocator
  color?: string
  label?: string
  label?: string
}

export type ResolvedChartSeries = {
  dataKey: string
  label: string
  color: string
  locator: DataTypeLocator
}

const humanizeFieldKey = (key: string): string =>
  key
    .split('_')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')

const dataKeyFromLocator = (loc: DataTypeLocator, index: number): string => {
  if (!loc || loc.length === 0) return `series_${index}`
  return loc.map(m => m[1]).join('_')
}

/** Row label for Recharts `nameKey`: scalar `data`, or object `data.value` when string. */
const chartCategoryLabelFromKeyNode = (
  keyNode: ContentNode | null | undefined,
): string => {
  if (!keyNode) return ''
  const raw = (keyNode as { data?: unknown }).data
  if (raw === null || raw === undefined) return ''
  if (
    typeof raw === 'string' ||
    typeof raw === 'number' ||
    typeof raw === 'boolean'
  ) {
    return String(raw)
  }
  if (typeof raw === 'object') {
    const v = (raw as Record<string, unknown>).value
    if (typeof v === 'string' && v.trim() !== '') return v.trim()
    return ''
  }
  return String(raw)
}

const getFieldLabelFromDataTypeConfig = (
  rowItemDataTypeConfig: DataTypeConfig | undefined,
  locator: DataTypeLocator,
): string | undefined => {
  if (!rowItemDataTypeConfig) return undefined

  return pipe(
    rowItemDataTypeConfig,
    DataTypeConfigUtils.find(locator),
    config => {
      if (!config) return undefined

      const fromState =
        typeof config.state?.label === 'string' ? config.state.label.trim() : ''
      if (fromState) return fromState

      const fromDesc =
        typeof (config as any).description === 'string'
          ? (config as any).description.trim()
          : ''
      return fromDesc || undefined
    },
  )
}

export type ChartTooltipExtraFieldConfig = {
  locator: DataTypeLocator
  label?: string
  label?: string
}

/** Navigates to a list view with pre-applied filters (AB#12933 dashboard snapshot charts). */
export type ChartViewDetailsConfig = {
  routeId: string
  viewUrlState: ViewUrlState
  label?: string
}

export type ChartWidgetProps = {
  /** Chart heading inside the widget (not the surrounding Card header). */
  title?: string
  /** Optional line below the title (see ChartSubtitle). */
  subtitle?: string
  chartType: ChartType
  contentNode?: ContentNode | null
  dimensionLocator?: DataTypeLocator
  axisLabelLocator?: DataTypeLocator
  _series?: ChartSeriesConfig[]
  series?: ChartSeriesConfig[]
  yAxisLabel?: string
  axisTitle?: string
  xAxisLabel?: string
  categoryAxisTitle?: string
  dataTopN?: number
  limitRows?: number
  stacked?: boolean
  lineLegendStyle?: boolean
  isLoading?: boolean
  /**
   * Extra numeric fields merged into each chart row (not shown in the legend).
   * Shown as extra lines on bar/line chart tooltips, e.g. cube `count` as "Container Count".
   */
  tooltipExtraFields?: ChartTooltipExtraFieldConfig[]
  /**
   * When true, shows numeric labels on bars: vertical = totals above each category;
   * horizontal = values at the end of each bar. Ignored for line/pie.
   */
  showBarValueLabels?: boolean
  showDataLabels?: boolean
  showLegend?: boolean
  /** When true, hides the external legend above the chart. Useful for single-series charts. */
  hideLegend?: boolean
  /**
   * When true, enables overflow scrolling for dense charts: horizontal scroll on vertical
   * bar and line charts; vertical scroll on horizontal bar charts. Default false.
   */
  scrollable?: boolean
  /**
   * [date-fns format](https://date.fns.org/v3/docs/format) for category axis values that
   * parse as dates (e.g. `yyyy MM`). Omit for default two-line ticks (`MMM` / `yyyy`) and
   * tooltip `MMM yyyy`. Use a quoted newline for multi-line ticks, e.g. `yyyy'\\n'MM`.
   */
  categoryAxisDateFormat?: string
  /**
   * When true, category keys are parsed as ISO datetimes for axis + tooltip (view config).
   * Default false leaves labels as plain strings.
   */
  categoryAxisIsDate?: boolean
  /**
   * Vertical bar category labels: `angled` (−30°, default) or `flat` (horizontal under bars).
   */
  categoryAxisLabelLayout?: 'flat' | 'angled'
  /**
   * Numeric formatting for on-chart labels, tooltip metric + extra values, and the
   * pie center total. Built on the shared `numberFormatter` utility used by TableWidget,
   * Sparkline, etc. (including `style: 'percent'`).
   *
   * Default precision is `1` — shows at most one decimal place and omits it
   * when unnecessary (`12` → `"12"`, `12.34` → `"12.3"`).
   *
   * Examples (from view configs / ChartValueFormat bridge):
   *   `{ precision: 2 }`                      -> "12.35"
   *   `{ precision: 0, useShorthand: true }`  -> "12K" (maps to numberDisplayFormat)
   *   `{ style: 'percent', precision: 1 }`    -> "27.3%"
   *   `{ suffix: ' days' }`                   -> "12.3 days"
   */
  valueFormat?: ChartValueFormat
  /**
   * `snapshot` — homepage FCL charts (Figma): Montserrat titles, 232px height,
   * thinner line / wider bars, tighter Y-axis label gap.
   */
  chartVariant?: ChartTitleVariant
  /**
   * Override plot height (px). Vertical bar / line: fixed height (default 300, or 232
   * for snapshot). Horizontal bar: minimum outer height the row count can grow beyond;
   * omit for fully content-driven height.
   */
  chartHeight?: number
  height?: number
  /**
   * Snapshot vertical bar width (px). Omit for default (`29`); By Carrier Figma uses `65`.
   */
  snapshotBarWidth?: number
  /**
   * Snapshot vertical bar angled X-axis band. Default `medium`; use `tall` for long
   * category labels (e.g. transshipment ports, shipment destinations).
   */
  snapshotAngledAxisBand?: SnapshotAngledAxisBand
  /**
   * When set on snapshot charts, shows a header link to the filtered parent list view.
   */
  viewDetails?: ChartViewDetailsConfig
}

const ChartWidget = ({
  title,
  subtitle,
  chartType,
  contentNode,
  dimensionLocator,
  axisLabelLocator,
  _series,
  series = [],
  yAxisLabel,
  axisTitle = '',
  xAxisLabel,
  categoryAxisTitle = '',
  dataTopN,
  limitRows,
  stacked = false,
  lineLegendStyle = false,
  isLoading = false,
  tooltipExtraFields,
  showBarValueLabels = false,
  showDataLabels,
  showLegend: showLegendProp,
  hideLegend: hideLegendProp = false,
  scrollable = false,
  categoryAxisDateFormat,
  categoryAxisIsDate = false,
  categoryAxisLabelLayout,
  valueFormat,
  chartVariant = 'default',
  chartHeight: chartHeightProp,
  height,
  snapshotBarWidth,
  snapshotAngledAxisBand,
  viewDetails,
}: ChartWidgetProps) => {
  const isSnapshot = chartVariant === 'snapshot'
  const getRouteById = useAppStore(store => store.router.getRouteById)
  const resolvedChartHeight =
    height ??
    chartHeightProp ??
    (isSnapshot ? SNAPSHOT_CHART_HEIGHT : CHART_HEIGHT)
  const resolvedDimensionLocator = dimensionLocator ?? axisLabelLocator
  const resolvedSeriesConfigs = _series ?? series
  const resolvedYAxisLabel = yAxisLabel ?? axisTitle
  const resolvedXAxisLabel = xAxisLabel ?? categoryAxisTitle
  const resolvedShowDataLabels = showDataLabels ?? showBarValueLabels
  const listContentNode = useMemo(
    () =>
      pipe(
        contentNode,
        Assert.orThrow(
          ContentNodeUtils.isKind('FIELD'),
          () => new Error('Chart content node must be a FIELD'),
        ),
        ContentNodeUtils.getOfType(),
        Assert.orThrow(
          ContentNodeUtils.isKind('LIST'),
          () => new Error('Chart content node ofType must be a LIST'),
        ),
      ),
    [contentNode],
  )

  const itemsContentNodeList: ContentNode[] = useMemo(
    () =>
      pipe(
        listContentNode,
        ContentNodeUtils.getItems(),
        Assert.orReturn(Value.isNotNullable, () => []),
      ),
    [listContentNode],
  )

  const rowItemDataTypeConfig = useMemo(
    () =>
      pipe(
        listContentNode,
        ContentNodeUtils.getOfType(),
        Assert.orThrow(
          ContentNodeUtils.isKind('OBJECT'),
          () => new Error('Chart list ofType must be an OBJECT'),
        ),
        ContentNodeUtils.getDataTypeConfig(),
      ),
    [listContentNode],
  )

  const processedRows = useMemo(
    () => pipe(itemsContentNodeList, List.limit(dataTopN ?? limitRows)),
    [dataTopN, itemsContentNodeList, limitRows],
  )

  const resolvedSeries = useMemo(() => {
    return pipe(
      resolvedSeriesConfigs,
      List.map((config, i) => {
        const locator = config.measureLocator ?? config.locator ?? []
        const dataKey = dataKeyFromLocator(locator, i)
        const label =
          config.label ??
          config.label ??
          getFieldLabelFromDataTypeConfig(
            rowItemDataTypeConfig,
            locator,
          )?.trim() ??
          humanizeFieldKey(dataKey)
        const explicit = config.color?.trim()
        const color =
          explicit ||
          inferenceColorForSeries(chartType, stacked, i) ||
          CHART_INFERENCE_COLOR_PALETTE[0]

        return { dataKey, label, color, locator }
      }),
    )
  }, [resolvedSeriesConfigs, rowItemDataTypeConfig, chartType, stacked])

  const nameKey = useMemo(() => {
    if (!resolvedDimensionLocator) return 'key'
    return dataKeyFromLocator(resolvedDimensionLocator, 0)
  }, [resolvedDimensionLocator])

  const chartValues = useMemo(() => {
    const baseRows = pipe(
      processedRows,
      List.map((rowNode: ContentNode) => {
        const entry: Record<string, string | number | undefined> = {}

        if (resolvedDimensionLocator) {
          const keyNode = ContentNodeUtils.findContentNode(
            resolvedDimensionLocator,
          )(rowNode)
          entry[nameKey] = chartCategoryLabelFromKeyNode(keyNode)
        } else {
          entry[nameKey] = ''
        }

        pipe(
          resolvedSeries,
          List.map((s: ResolvedChartSeries) => {
            const node = ContentNodeUtils.findContentNode(s.locator)(rowNode)
            const targetNode =
              node?.kind === 'FIELD' ? (node as any).ofType : node
            const isNumeric =
              targetNode?.typeName === 'Int' || targetNode?.typeName === 'Float'
            const raw = node?.data
            const n = isNumeric
              ? Number(raw)
              : typeof raw === 'number'
                ? raw
                : 0
            entry[s.dataKey] = Number.isFinite(n) ? n : 0
            return null
          }),
        )

        pipe(
          tooltipExtraFields ?? [],
          List.map((f, i) => {
            const dk = dataKeyFromLocator(f.locator, resolvedSeries.length + i)
            const node = ContentNodeUtils.findContentNode(f.locator)(rowNode)
            const targetNode =
              node?.kind === 'FIELD' ? (node as any).ofType : node
            const isNumeric =
              targetNode?.typeName === 'Int' || targetNode?.typeName === 'Float'
            const raw = node?.data
            const n = isNumeric
              ? Number(raw)
              : typeof raw === 'number'
                ? raw
                : 0
            entry[dk] = Number.isFinite(n) ? n : 0
            return null
          }),
        )

        return entry
      }),
    )

    if (chartType === 'line' && categoryAxisIsDate) {
      return sortChartRowsByCategoryDate(
        baseRows as Record<string, unknown>[],
        nameKey,
      ) as typeof baseRows
    }

    return baseRows
  }, [
    processedRows,
    resolvedDimensionLocator,
    nameKey,
    resolvedSeries,
    tooltipExtraFields,
    chartType,
    categoryAxisIsDate,
  ])

  const floatingTooltipExtraRows = useMemo(
    () =>
      (tooltipExtraFields ?? []).map((f, i) => ({
        dataKey: dataKeyFromLocator(f.locator, resolvedSeries.length + i),
        label:
          f.label ??
          f.label ??
          humanizeFieldKey(String(f.locator.at(-1)?.[1] ?? '')),
      })),
    [tooltipExtraFields, resolvedSeries],
  )

  const chartValuesForHorizontal = useMemo((): ChartData['chartValues'] => {
    if (chartType !== 'horizontal') return chartValues
    const dk = resolvedSeries[0]?.dataKey
    if (!dk) return chartValues
    return pipe(
      chartValues as ChartValue[],
      List.map((row: ChartValue) => ({
        ...row,
        value: Number(row[dk]) || 0,
      })),
    )
  }, [chartType, chartValues, resolvedSeries])

  const chartData = useMemo(
    (): ChartData => ({
      yAxisLabel: resolvedYAxisLabel,
      xAxisLabel: resolvedXAxisLabel,
      chartValues:
        chartType === 'horizontal' ? chartValuesForHorizontal : chartValues,
      nameKey,
      valueKey:
        chartType === 'horizontal' ? 'value' : resolvedSeries[0]?.dataKey,
      categoryAxisDateFormat,
      categoryAxisIsDate,
      categoryAxisLabelLayout,
      // Homepage snapshot only — Port-to-Port and other charts use default bar layout.
      snapshotBarWidth: isSnapshot ? snapshotBarWidth : undefined,
      snapshotAngledAxisBand: isSnapshot ? snapshotAngledAxisBand : undefined,
      valueFormat,
    }),
    [
      resolvedYAxisLabel,
      categoryAxisDateFormat,
      categoryAxisIsDate,
      categoryAxisLabelLayout,
      resolvedXAxisLabel,
      chartType,
      chartValues,
      chartValuesForHorizontal,
      isSnapshot,
      nameKey,
      resolvedSeries,
      snapshotAngledAxisBand,
      snapshotBarWidth,
      valueFormat,
    ],
  )

  const showLegend = (showLegendProp ?? !hideLegendProp) && chartType !== 'pie'

  const legendVStack = showLegend ? (
    <VStack alignItems="flex-start" spacing="8px">
      {resolvedSeries.map(item =>
        lineLegendStyle ? (
          <ChartLineLegendItem
            key={item.dataKey}
            color={item.color}
            label={item.label}
          />
        ) : (
          <ChartBarLegendItem
            key={item.dataKey}
            color={item.color}
            label={item.label}
          />
        ),
      )}
    </VStack>
  ) : null

  const viewDetailsLink = useMemo(() => {
    if (!isSnapshot || !viewDetails) return null

    const route = getRouteById(viewDetails.routeId)
    const listPath = route?.path
    if (!listPath) return null

    const linkLabel = viewDetails.label ?? SNAPSHOT_VIEW_IN_LIST_LABEL
    const testIdSlug = title
      ? title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')
      : 'chart'

    return (
      <Link
        to={listPath}
        search={{ viewState: viewDetails.viewUrlState }}
        display="inline-flex"
        alignItems="center"
        flexShrink={0}
        color="presets.turquoise.30"
        fontSize="12px"
        fontWeight="700"
        fontFamily={`'Montserrat', sans-serif`}
        lineHeight="normal"
        textDecoration="none"
        whiteSpace="nowrap"
        _hover={{ textDecoration: 'underline' }}
        data-testid={`chart-view-details-${testIdSlug}`}
      >
        {linkLabel}
        <Icon
          icon="ChevronRightIcon"
          boxSize="20px"
          color="presets.turquoise.30"
        />
      </Link>
    )
  }, [getRouteById, isSnapshot, title, viewDetails])

  const hasHeaderExtras = Boolean(viewDetailsLink || legendVStack)

  const titleContent =
    title || subtitle ? (
      subtitle ? (
        <VStack
          alignItems="flex-start"
          spacing="4px"
          minW={`${CHART_TITLE_MIN_WIDTH}px`}
          pl={isSnapshot ? SNAPSHOT_CHART_TITLE_PADDING_LEFT : undefined}
        >
          {title ? (
            <ChartTitle variant={chartVariant}>{title}</ChartTitle>
          ) : null}
          <ChartSubtitle>{subtitle}</ChartSubtitle>
        </VStack>
      ) : (
        <ChartTitle
          variant={chartVariant}
          pl={isSnapshot ? SNAPSHOT_CHART_TITLE_PADDING_LEFT : undefined}
        >
          {title}
        </ChartTitle>
      )
    ) : null

  const hasRenderableChartData = itemsContentNodeList.length > 0
  const showLoadingOverlay = isLoading && !hasRenderableChartData

  const chartHeader =
    titleContent && hasHeaderExtras ? (
      <Flex
        flexWrap="wrap"
        alignItems={subtitle ? 'flex-start' : 'center'}
        columnGap="16px"
        rowGap="12px"
        minW={0}
      >
        <Box flex={`0 1 ${CHART_TITLE_MIN_WIDTH}px`} minW={0}>
          {titleContent}
        </Box>
        <Box flex="1 1 0" minW={0} aria-hidden />
        {viewDetailsLink ? (
          <Box flex="0 0 auto" minW={0}>
            {viewDetailsLink}
          </Box>
        ) : null}
        {legendVStack ? (
          <Box flex="0 0 auto" minW={0}>
            {legendVStack}
          </Box>
        ) : null}
      </Flex>
    ) : titleContent && !hasHeaderExtras ? (
      titleContent
    ) : !titleContent && hasHeaderExtras ? (
      <HStack justifyContent="flex-end" alignItems="flex-start" gap="16px">
        {viewDetailsLink}
        {legendVStack}
      </HStack>
    ) : null

  // if (error) return <Text color="presets.pink.70">Error loading chart</Text>

  const chartBody =
    chartType === 'verticalBar' ? (
      <VerticalBarChart
        chartData={chartData}
        hideLegend
        stacked={stacked}
        series={resolvedSeries.map(s => ({
          name: s.label,
          dataKey: s.dataKey,
          color: s.color,
        }))}
        floatingTooltipExtraRows={floatingTooltipExtraRows}
        showBarValueLabels={resolvedShowDataLabels}
        horizontalScroll={scrollable}
        chartHeight={resolvedChartHeight}
        variant={chartVariant}
      />
    ) : chartType === 'line' ? (
      <LineChart
        chartData={chartData}
        hideLegend
        lineSeries={resolvedSeries.map(s => ({
          dataKey: s.dataKey,
          name: s.label,
        }))}
        lineColors={resolvedSeries.map(s => s.color)}
        floatingTooltipExtraRows={floatingTooltipExtraRows}
        horizontalScroll={scrollable}
        chartHeight={resolvedChartHeight}
        variant={chartVariant}
      />
    ) : chartType === 'horizontal' ? (
      <HorizontalChart
        chartData={chartData}
        valueAxisLabel={resolvedYAxisLabel}
        legendLabel={resolvedSeries[0]?.label ?? ''}
        barFillColor={resolvedSeries[0]?.color}
        barSize={HORIZONTAL_BAR_SIZE}
        hideLegend
        showBarValueLabels={resolvedShowDataLabels}
        minChartHeight={height ?? chartHeightProp}
      />
    ) : (
      <PieChart
        chartData={chartData}
        tooltipValueLabel={resolvedSeries[0]?.label}
      />
    )

  return (
    <Box
      position="relative"
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      gap={isSnapshot ? SNAPSHOT_CHART_TITLE_CONTENT_GAP : '16px'}
      minW={0}
    >
      {chartHeader}
      <Box flex="1" minW={0} position="relative">
        {chartBody}
        {showLoadingOverlay ? (
          <Center
            position="absolute"
            inset={0}
            pointerEvents="none"
            aria-hidden
          >
            <Spinner size="sm" color="presets.turquoise.100" />
          </Center>
        ) : null}
      </Box>
    </Box>
  )
}

export const ChartWidgetDefinition = createWidget(ChartWidget, {
  type: 'ChartWidget',
})
